import type { DBEquipmentUsage } from "@/lib/db-types";
import type { EquipmentStatus } from "./constants";
import { EQUIPMENT_STATUS_LABELS } from "./constants";

/**
 * Tilgjengelighet og dobbelbooking for utstyr (VIB-2919). Ren logikk over
 * bookinglinjer. Datoer er ISO-datoer (YYYY-MM-DD), og til-datoen er inklusiv.
 *
 * Konfliktregelen speiler viewet equipment_booking_conflicts: to linjer på
 * samme utstyr er i konflikt når begge dekker en dag der sum antall er større
 * enn det vi eier. Dobbelbooking er en advarsel, ikke en sperre.
 */

export interface BookingLine {
  id: string;
  booking_id: string;
  item_id: string;
  quantity: number;
  date_from: string;
  date_to: string;
  /** Linjer fra viewet er alltid aktive. Avbestilte linjer teller ikke. */
  status?: "active" | "cancelled";
}

export interface DateRange {
  from: string;
  to: string;
}

export interface LineConflict {
  lineId: string;
  otherLineId: string;
  itemId: string;
  firstDay: string;
  lastDay: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function toUtc(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function toIso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  return toIso(toUtc(iso) + days * DAY_MS);
}

/** Alle dager fra og med `from` til og med `to`. */
export function eachDay(from: string, to: string): string[] {
  const days: string[] = [];
  for (let t = toUtc(from), end = toUtc(to); t <= end; t += DAY_MS) days.push(toIso(t));
  return days;
}

export function rangesOverlap(a: DateRange, b: DateRange): boolean {
  return a.from <= b.to && b.from <= a.to;
}

function isActive(line: BookingLine): boolean {
  return line.status !== "cancelled";
}

/** Sum bookede per dag for ett utstyr, eventuelt begrenset til et vindu. */
export function bookedPerDay(lines: BookingLine[], itemId: string, window?: DateRange): Map<string, number> {
  const perDay = new Map<string, number>();
  for (const line of lines) {
    if (line.item_id !== itemId || !isActive(line)) continue;
    const from = window && window.from > line.date_from ? window.from : line.date_from;
    const to = window && window.to < line.date_to ? window.to : line.date_to;
    if (from > to) continue;
    for (const day of eachDay(from, to)) perDay.set(day, (perDay.get(day) ?? 0) + line.quantity);
  }
  return perDay;
}

/** Laveste ledige antall over perioden (aldri under 0). */
export function availableQuantity(owned: number, lines: BookingLine[], itemId: string, period: DateRange): number {
  const perDay = bookedPerDay(lines, itemId, period);
  let maxBooked = 0;
  for (const n of perDay.values()) maxBooked = Math.max(maxBooked, n);
  return Math.max(0, owned - maxBooked);
}

/** Linjepar i konflikt, i begge retninger (som viewet). `owned` er eid antall per utstyr. */
export function bookingConflicts(lines: BookingLine[], owned: Map<string, number>): LineConflict[] {
  const active = lines.filter(isActive);
  const byItem = new Map<string, BookingLine[]>();
  for (const line of active) byItem.set(line.item_id, [...(byItem.get(line.item_id) ?? []), line]);

  const conflicts: LineConflict[] = [];
  for (const [itemId, itemLines] of byItem) {
    const quantity = owned.get(itemId);
    if (quantity === undefined || itemLines.length < 2) continue;
    const overbooked = [...bookedPerDay(itemLines, itemId)]
      .filter(([, n]) => n > quantity)
      .map(([day]) => day)
      .sort();
    if (overbooked.length === 0) continue;
    for (const a of itemLines) {
      for (const o of itemLines) {
        if (a.id === o.id) continue;
        const shared = overbooked.filter((d) => d >= a.date_from && d <= a.date_to && d >= o.date_from && d <= o.date_to);
        if (shared.length > 0) {
          conflicts.push({ lineId: a.id, otherLineId: o.id, itemId, firstDay: shared[0], lastDay: shared[shared.length - 1] });
        }
      }
    }
  }
  return conflicts;
}

/** «28.–30.10», «29.10» eller «30.10.–2.11». */
export function formatDayRange(from: string, to: string): string {
  const [, fm, fd] = from.split("-").map(Number);
  const [, tm, td] = to.split("-").map(Number);
  if (from === to) return `${fd}.${fm}`;
  if (fm === tm) return `${fd}.–${td}.${tm}`;
  return `${fd}.${fm}.–${td}.${tm}`;
}

export type BookingStateKind = "unavailable" | "conflict" | "booked" | "partial" | "free";

export interface BookingState {
  kind: BookingStateKind;
  label: string;
}

interface StateItem {
  id: string;
  quantity: number;
  status: EquipmentStatus;
}

/** Sammenhengende dager (sortert) som ett spenn, for etiketten. */
function firstSpan(days: string[]): DateRange {
  let end = days[0];
  for (const d of days.slice(1)) {
    if (d !== addDays(end, 1)) break;
    end = d;
  }
  return { from: days[0], to: end };
}

/**
 * Status i utstyrslista: på service o.l. først, så dobbelbooking, booket,
 * delvis ledig (mengdevare) eller ledig. Uten periode gjelder det fra i dag og fremover.
 */
export function bookingState(item: StateItem, lines: BookingLine[], period: DateRange | null, today: string): BookingState {
  if (item.status !== "ok") return { kind: "unavailable", label: EQUIPMENT_STATUS_LABELS[item.status] };

  const window: DateRange = period ?? { from: today, to: "9999-12-31" };
  const perDay = [...bookedPerDay(lines, item.id, window)].sort(([a], [b]) => a.localeCompare(b));
  if (perDay.length === 0) return { kind: "free", label: "Ledig" };

  const over = perDay.filter(([, n]) => n > item.quantity).map(([d]) => d);
  if (over.length > 0) {
    const span = firstSpan(over);
    return { kind: "conflict", label: `⚠ Dobbeltbooket ${formatDayRange(span.from, span.to)}` };
  }

  const full = perDay.filter(([, n]) => n >= item.quantity).map(([d]) => d);
  if (full.length > 0) {
    const span = firstSpan(full);
    return { kind: "booked", label: `Booket ${formatDayRange(span.from, span.to)}` };
  }

  const [peakDay, peak] = perDay.reduce((best, cur) => (cur[1] > best[1] ? cur : best));
  return {
    kind: "partial",
    label: `${item.quantity - peak} av ${item.quantity} ledig ${formatDayRange(peakDay, peakDay)}`,
  };
}

/** «Ledig i periode»: tilgjengelig og minst ett ledig alle dager i perioden. */
export function isFreeInPeriod(item: StateItem, lines: BookingLine[], period: DateRange): boolean {
  return item.status === "ok" && availableQuantity(item.quantity, lines, item.id, period) >= 1;
}

/** «3 dager · 1 booking», «0 dager». */
export function formatUsage(usage: DBEquipmentUsage | undefined): string {
  const days = usage?.booked_days ?? 0;
  const bookings = usage?.bookings ?? 0;
  const dayText = `${days} ${days === 1 ? "dag" : "dager"}`;
  return bookings > 0 ? `${dayText} · ${bookings} ${bookings === 1 ? "booking" : "bookinger"}` : dayText;
}
